'use client';

/**
 * Tips Dropdown Component
 *
 * Collapsible section with generation tips for better results.
 */
import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// Tips for better generation results with colored labels
const GENERATION_TIPS = [
  {
    labelColor: 'text-green-400',
    dotColor: 'bg-green-400',
    label: 'Use comma-separated tags:',
    description: 'Keep tags 1-4 words each, not full sentences or paragraphs.',
  },
  {
    labelColor: 'text-blue-400',
    dotColor: 'bg-blue-400',
    label: 'Be specific:',
    description:
      'Include setting, clothing details, pose, lighting, atmosphere, background, and composition style tags.',
  },
  {
    labelColor: 'text-purple-400',
    dotColor: 'bg-purple-400',
    label: 'Example:',
    description:
      '"modern outdoor pool, night time, dark green skinny bikini, standing pose by poolside, ambient blue lighting, elegant evening atmosphere, modern villa background, subtle city lights, cinematic composition"',
  },
  {
    labelColor: 'text-pink-400',
    dotColor: 'bg-pink-400',
    label: 'Note:',
    description:
      'Results may vary - AI image generation cannot guarantee perfect or consistent results across multiple images.',
  },
];

export function TipsDropdown() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#1a1a2e]">
      {/* Header - Clickable to expand */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-white/5"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-500">
            <span className="text-xs font-bold text-white">!</span>
          </div>
          <span className="text-sm font-semibold text-orange-400">
            Tips for Better Results
          </span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-3 px-4 pt-2 pb-4">
              {GENERATION_TIPS.map((tip, index) => (
                <motion.div
                  key={tip.label}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-3 rounded-xl bg-[#252540] p-4"
                >
                  {/* Colored dot */}
                  <div
                    className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${tip.dotColor}`}
                  />
                  <p className="text-sm leading-relaxed">
                    <span className={`font-semibold ${tip.labelColor}`}>
                      {tip.label}
                    </span>{' '}
                    <span className="text-gray-300">{tip.description}</span>
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
